import { useMutation } from '@apollo/client';
import {
  FULL_POINTS_REDEMPTION,
  ROLLBACK_LOYALTY_ACTIVITY,
  CONFIRM_BOOKING,
  ADD_NOTE_TO_BOOKING,
  REDEEM_TICKET_EXCHANGES
} from 'store/mutations';
import { buildPointsRedemptionLoyaltyData, loyaltyPointsDifferencesCreationAndCancelLegs } from 'utils/booking';
import { getTotalPoints } from 'components/ui/BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.utils';
import { productFamilyCodes } from 'types/productFamilyCodes';

import { useManagedMutation } from '../../../utils/managedHooks';
import { getRedeemTicketsData } from '../../../utils/exchange';
import { getCarrier } from '../../../pages/Exchange/Exchange.utils';

const usePayIfLoyaltyAndConfirmBookingMutation = () => {
  const [redeemLoyaltyPoints, { loading: redeemLoyaltyPointsLoading, error: redeemLoyaltyPointsError }] =
    useMutation(FULL_POINTS_REDEMPTION);
  const [rollbackActivity, { loading: rollbackActivityLoading, error: rollbackActivityError }] = useMutation(ROLLBACK_LOYALTY_ACTIVITY);
  const [confirmBooking, { loading: confirmBookingLoading, error: confirmBookingError }] = useMutation(CONFIRM_BOOKING);
  const [addNote, { loading: addNoteLoading }] = useMutation(ADD_NOTE_TO_BOOKING);
  const [redeemTicketExchanges, { loading: redeemTicketExchangesLoading }] = useManagedMutation(REDEEM_TICKET_EXCHANGES);

  const handleMutation = async ({
    variables: {
      membershipNumber,
      bookingData,
      isExchange,
      isMissedDeparture,
      isFlowForwardCapped,
      sendConfirmationEmail,
      shouldWaivePoints = false,
      actionBy
    }
  }) => {
    const { productFamilies } = bookingData;
    const totalPoints = getTotalPoints(bookingData);
    const isLoyaltyLoggedOut =
      productFamilies?.map(({ familyGroup }) => familyGroup).includes(productFamilyCodes.EUROSTAR_LOYALTY) && !membershipNumber;
    let pointsRedemptionResult = null;
    let redeemTicketExchangeActivityId = null;

    try {
      if (isLoyaltyLoggedOut) {
        const hasProvisionalExtras =
          (bookingData.outboundExtras || []).concat(bookingData.inboundExtras || []).filter((e) => !e.cancelled && e.provisional).length >
          0;

        if (!hasProvisionalExtras) {
          throw new Error('Loyalty account not found. Please log in on Club Eurostar first');
        }
      }

      if (membershipNumber && isExchange) {
        const loyaltyPointsDifference = loyaltyPointsDifferencesCreationAndCancelLegs(bookingData);

        const displayedLoyaltyPointsDifference = !shouldWaivePoints ? loyaltyPointsDifference : 0;

        if (displayedLoyaltyPointsDifference && displayedLoyaltyPointsDifference > 0) {
          const carrier = getCarrier(bookingData);
          const redeemTicketExchangeResult = await redeemTicketExchanges({
            variables: {
              membershipNumber,
              bookingReference: bookingData.reference,
              carrier,
              redeemTickets: getRedeemTicketsData(bookingData),
              actionBy
            }
          });

          if (redeemTicketExchangeResult.data?.redeemTicketExchanges) {
            redeemTicketExchangeActivityId = redeemTicketExchangeResult.data.redeemTicketExchanges.transactionJournalId;

            await addNote({
              variables: {
                note: `Exchange of tickets for Club Eurostar account: ${membershipNumber}, totalPointsRedeemed: ${Math.abs(
                  redeemTicketExchangeResult.data.redeemTicketExchanges.totalPointsCost
                )}`,
                bookingReference: bookingData.reference
              }
            });
          }
        } else if (displayedLoyaltyPointsDifference === 0 && loyaltyPointsDifference > 0) {
          await addNote({
            variables: {
              note: `Points waived, totalPointsWaived: ${Math.abs(loyaltyPointsDifference)}`,
              bookingReference: bookingData.reference
            }
          });
        }
      } else if (membershipNumber && totalPoints) {
        const variables = buildPointsRedemptionLoyaltyData({
          membershipNumber,
          booking: bookingData,
          useNewLoyaltySystem: true,
          actionBy
        });

        const { data } = await redeemLoyaltyPoints({
          variables
        });
        pointsRedemptionResult = data.fullPointsRedemption;
      }

      const confirmBookingResponse = await confirmBooking({
        variables: {
          bookingReference: bookingData.reference,
          sendConfirmationEmail,
          isMissedDeparture,
          isFlowForwardCapped,
          language: bookingData.language
        }
      });

      if (pointsRedemptionResult) {
        try {
          const points = pointsRedemptionResult.pointsSpent;
          await addNote({
            variables: {
              note: `Purchase of tickets for Club Eurostar account: ${membershipNumber}, totalPointsSpent: ${points}`,
              bookingReference: bookingData.reference
            }
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`Failed to add note for points redemption, booking: ${bookingData.reference}`, err);
        }
      }

      return confirmBookingResponse;
    } catch (err) {
      if (pointsRedemptionResult || redeemTicketExchangeActivityId) {
        try {
          await rollbackActivity({
            variables: {
              activityId: pointsRedemptionResult?.activityId || redeemTicketExchangeActivityId,
              useNewLoyaltySystem: true,
              actionBy
            }
          });
        } catch (error) {
          alert(
            pointsRedemptionResult
              ? 'The rollback of points redemption failed, so please try this manually'
              : 'The rollback of redeem exchange ticket failed, so please try this manually'
          );
        }
      }
      throw err;
    }
  };

  return [
    handleMutation,
    {
      loading:
        redeemLoyaltyPointsLoading || confirmBookingLoading || rollbackActivityLoading || redeemTicketExchangesLoading || addNoteLoading,
      error: redeemLoyaltyPointsError || confirmBookingError || rollbackActivityError
    }
  ];
};

export default usePayIfLoyaltyAndConfirmBookingMutation;
